import { Component, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { ValidationProblem, Order, OrderManagerService, ValidationService, OrderRepositoryService, PatchDocument, ePartySubType, instantValidationThrottle } from 'reg-hub-common';
import { QCLienDetailsComponent } from '../lien-details/qc-lien-details/qc-lien-details.component';
import { MatDialog } from '@angular/material/dialog';
import { CopyPartiesComponent } from '../copy-parties/copy-parties.component';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { PageEvent } from '@angular/material/paginator';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-assignment-details',
    templateUrl: './assignment-details.component.html',
    styleUrl: './assignment-details.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AssignmentDetailsComponent extends SteppedComponent {
  private readonly transloco = inject(TranslocoService);
  @ViewChild(QCLienDetailsComponent) qcLienDetailsComponent!: QCLienDetailsComponent;
  @ViewChild('assigneeRegistrationNumberInput') assigneeRegistrationNumberInput!: AbstractControl;

  protected displayedColumns = ['number', 'actions'];
  protected registrationNumbers!: FormGroup;
  
  assignorRegistrationNumbers: string[] = [];
  assigneeRegistrationNumbers: string[] = [];
  
  assignorRegNumsPageIndex = 0;
  assigneeRegNumsPageIndex = 0;
  pageSize = 10;

  assignorRegistrationNumberError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);
  assigneeRegistrationNumberError$: BehaviorSubject<string | null | undefined> = new BehaviorSubject<string | null | undefined>(null);

  constructor(
    orderManager: OrderManagerService,
    validationService: ValidationService,
    protected orderRepository: OrderRepositoryService,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private messageService: ToastrService) {
    super(orderManager, validationService);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    
    this.orderManager.currentOrder
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.onAssignorRegistrationNumberPaging();
        this.onAssigneeRegistrationNumberPaging();
      });

    this.registrationNumbers = this.formBuilder.group({
      assignorRegistrationNumber: [''],
      assigneeRegistrationNumber: ['']
    });

    this.registrationNumbers.get('assignorRegistrationNumber')?.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(val => this.onAssignorRegistrationNumberValueChanged(val));
    this.registrationNumbers.get('assigneeRegistrationNumber')?.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(val => this.onAssigneeRegistrationNumberValueChanged(val));
  }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return StepValidationState.ValidationSuccess;
  }

  public override onSaving(): void {
    this.qcLienDetailsComponent.onSaving();
  }

  public override pushErrors(errors: ValidationProblem[] | undefined): void {
    if (!errors) {
      return;
    }

    const assignorRegistrationNumberErrors = errors?.filter(error => error.path.includes('/assignorregistrationnumbers') && error.userFriendlyMessage?.includes('format'));
    const assigneeRegistrationNumberErrors = errors?.filter(error => error.path.includes('/assigneeregistrationnumbers') && error.userFriendlyMessage?.includes('format'));
    this.assignorRegistrationNumberError$.next(assignorRegistrationNumberErrors.at(0)?.userFriendlyMessage);
    this.assigneeRegistrationNumberError$.next(assigneeRegistrationNumberErrors.at(0)?.userFriendlyMessage);
  }

  protected openCopyPartiesToAssignorsDialog(num: string) {
    this.dialog.open(CopyPartiesComponent, {
      data: { number: num, order: this.order, partySubType: ePartySubType.assignor, orderUIConfiguration: this.uiConfiguration },
      minWidth: '800px',
      minHeight: '400px'
    })
  }

  protected openCopyPartiesToAssigneesDialog(num: string) {
    this.dialog.open(CopyPartiesComponent, {
      data: { number: num, order: this.order, partySubType: ePartySubType.assignee, orderUIConfiguration: this.uiConfiguration },
      minWidth: '800px',
      minHeight: '400px'
    })
  }

  protected onAssignorRegistrationNumberValueChanged(val: string) {
    if (val) {
      let orderToValidate = this.copyOrder(this.order);
      orderToValidate.lien!.assignorRegistrationNumbers = [val.trim()];
      this.validate(orderToValidate);
    } else {
      this.assignorRegistrationNumberError$.next(null);
    }
  }

  protected onAssigneeRegistrationNumberValueChanged(val: string) {
    if (val) {
      let orderToValidate = this.copyOrder(this.order);
      orderToValidate.lien!.assigneeRegistrationNumbers = [val.trim()];
      this.validate(orderToValidate);
    } else {
      this.assigneeRegistrationNumberError$.next(null);
    }
  }

  protected addAssignorRegistrationNumbers(numbers?: string[]) {
    this.addRegistrationNumbers('assignor', numbers);
  }

  protected addAssigneeRegistrationNumbers(numbers?: string[]) {
    this.addRegistrationNumbers('assignee', numbers);
  }
  
  protected addRegistrationNumbers(type: 'assignor' | 'assignee', numbers?: string[]) {
    if (!numbers) {
      const control = this.registrationNumbers.get(`${type}RegistrationNumber`)
      numbers = [control?.value];
      control?.reset();
    }

    numbers = numbers.filter(n => !!n);
    
    if (numbers.length === 0) {
      return;
    }

    let patchDocument: PatchDocument[] = [
      {
        op: '', value: '', path: `Lien/QCLienDetails/${type}RegistrationNumbers/`
      }
    ]

    let currentRegistrationNumbers: string[] | null | undefined;

    if (type === 'assignor') {
      currentRegistrationNumbers = this.order.lien?.assignorRegistrationNumbers;
    } else {
      currentRegistrationNumbers = this.order.lien?.assigneeRegistrationNumbers;
    }

    if ((currentRegistrationNumbers?.length ?? 0) === 0) {
      patchDocument[0].op = 'add';
      patchDocument[0].value = numbers;
    } else if (numbers.length === 1) {
      patchDocument[0].op = 'add';
      patchDocument[0].path += '-';
      patchDocument[0].value = numbers.at(0)!;
    } else {
      patchDocument[0].op = 'replace';
      patchDocument[0].value = [...currentRegistrationNumbers!, ...numbers];
    }

    this.orderRepository.patchOrderReturnOrder(this.order.id, patchDocument)
      .subscribe(response => {
        const assignorEnd = (this.assignorRegNumsPageIndex * this.pageSize) + this.pageSize;

        if(assignorEnd < (response.resource.lien?.assignorRegistrationNumbers?.length ?? 0)) {
          this.assignorRegNumsPageIndex++;
        }

        const assigneeEnd = (this.assigneeRegNumsPageIndex * this.pageSize) + this.pageSize;

        if(assigneeEnd < (response.resource.lien?.assigneeRegistrationNumbers?.length ?? 0)) {
          this.assigneeRegNumsPageIndex++;
        }
        
        this.orderManager.updateOrder(response.resource)
      });
  }

  protected removeAssignorRegistrationNumber(index: number) {
    index += this.assignorRegNumsPageIndex * this.pageSize;
    
    let patchDocument = [
      {
        op: 'remove', value: index, path: `Lien/QCLienDetails/AssignorRegistrationNumbers/${index}`
      }
    ]

    this.orderRepository.patchOrderReturnOrder(this.order.id, patchDocument)
      .subscribe(response => this.orderManager.updateOrder(response.resource));
  }

  protected removeAssigneeRegistrationNumber(index: number) {
    index += this.assigneeRegNumsPageIndex * this.pageSize;
    
    let patchDocument = [
      {
        op: 'remove', value: index, path: `Lien/QCLienDetails/AssigneeRegistrationNumbers/${index}`
      }
    ]

    this.orderRepository.patchOrderReturnOrder(this.order.id, patchDocument)
      .subscribe(response => this.orderManager.updateOrder(response.resource));
  }

  protected onAssignorRegistrationNumbersFileSelected(event: Event) {
    this.onFileSelected(event, 'assignor');
  }

  protected onAssigneeRegistrationNubmersFileSelected(event: Event) {
    this.onFileSelected(event, 'assignee');
  }
  
  protected onFileSelected(event: Event, type: string) {
    const element = event.currentTarget as HTMLInputElement;
    let file = element.files ? element.files[0] : null;

    if (file) {
      const reader = new FileReader();

      reader.onload = () => {
        let numbers = reader.result?.toString().split('\n').map(n => n.trim());

        if (file.type === 'text/csv' ||
          file.type === 'application/csv' ||
          file.type === 'application/vnd.ms-excel') {
          numbers = numbers?.slice(1, numbers.length);
        } else if (file.type === 'text/plain') {
          //empty on purpose
        } else {
          this.messageService.error(this.transloco.translate('orders.uploadedFileTypeIsInvalidPlease'));
          return
        }

        if (type === 'assignor') {
          this.addAssignorRegistrationNumbers(numbers);
        } else if (type === 'assignee') {
          this.addAssigneeRegistrationNumbers(numbers);
        }
      }

      reader.readAsText(file);
    }
  }

  protected onClearAllAssignorRegistrationNumbers() {
    this.onClearAll('assignor');
  }
  
  protected onClearAllAssigneeRegistrationNumbers() {
    this.onClearAll('assignee');
  }

  protected onClearAll(type: 'assignor' | 'assignee') {
    let patchDocument: PatchDocument[] = [
      {
        op: 'replace', value: [], path: `Lien/QCLienDetails/${type}RegistrationNumbers/`
      }
    ]

    this.orderRepository.patchOrderReturnOrder(this.order.id, patchDocument)
      .subscribe(response => this.orderManager.updateOrder(response.resource));
  }

  protected getAssignorRegistrationNumbersLabel() {
    const label = this.uiConfiguration.lienDetailsConfiguration?.assignorRegistrationNumbersTitle ?? this.transloco.translate<string>('orders.assignorRegistrationNumbers');

    if (label.endsWith('s')) {
      return label.substring(0, label.length - 1);
    } else {
      return label;
    }
  }
  
  protected onAssignorRegistrationNumberPaging(e?: PageEvent) {
    this.assignorRegNumsPageIndex = e?.pageIndex ?? this.assignorRegNumsPageIndex;
    const length = this.order.lien?.assignorRegistrationNumbers?.length ?? 0;
    let start = this.assignorRegNumsPageIndex * this.pageSize;
    let end = start + this.pageSize;
    
    if(start >= length) {
      this.assignorRegNumsPageIndex--;
      
      if(this.assignorRegNumsPageIndex < 0) {
        this.assignorRegNumsPageIndex = 0;
      }

      start = this.assignorRegNumsPageIndex * this.pageSize;
      end = start + this.pageSize;
    }

    this.assignorRegistrationNumbers = this.order.lien?.assignorRegistrationNumbers?.slice(start, end) ?? [];
  }
  
  protected onAssigneeRegistrationNumberPaging(e?: PageEvent) {
    this.assigneeRegNumsPageIndex = e?.pageIndex ?? this.assigneeRegNumsPageIndex;
    const length = this.order.lien?.assigneeRegistrationNumbers?.length ?? 0;
    let start = this.assigneeRegNumsPageIndex * this.pageSize;
    let end = start + this.pageSize;
    
    if(start >= length) {
      this.assigneeRegNumsPageIndex--;

      if(this.assigneeRegNumsPageIndex < 0) {
        this.assigneeRegNumsPageIndex = 0;
      }

      start = this.assigneeRegNumsPageIndex * this.pageSize;
      end = start + this.pageSize;
    }

    this.assigneeRegistrationNumbers = this.order.lien?.assigneeRegistrationNumbers?.slice(start, end) ?? [];
  }
}
